// Simple analytics utility that respects cookie consent
// This is a simplified version to avoid TypeScript complexity

export const checkConsent = (category: string): boolean => {
    try {
        const consent = localStorage.getItem('anathoth_cookie_consent');
        if (consent) {
            const parsed = JSON.parse(consent);
            return parsed[category] === true;
        }
    } catch (error) {
        console.error('Error checking consent:', error);
    }
    return false;
};

// Google Analytics helpers
export const initGoogleAnalytics = (measurementId: string) => {
    if (!checkConsent('analytics')) return;
    
    // Prevent duplicate initialization
    if ((window as any).gtag) return;
    
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).gtag = function() {
        (window as any).dataLayer.push(arguments);
    };
    (window as any).gtag('js', new Date());
    (window as any).gtag('config', measurementId, {
        anonymize_ip: true,
        cookie_flags: 'SameSite=Lax;Secure'
    });
    
    console.log('Google Analytics initialized with ID:', measurementId);
};

export const trackPageView = (path?: string) => {
    if (!checkConsent('analytics')) return;
    if ((window as any).gtag) {
        (window as any).gtag('event', 'page_view', {
            page_path: path || window.location.pathname
        });
    }
};

export const trackEvent = (eventName: string, parameters: any = {}) => {
    if (!checkConsent('analytics')) return;
    if ((window as any).gtag) {
        (window as any).gtag('event', eventName, parameters);
    }
};

// Facebook Pixel helpers (disabled for now since you're only using GA)
export const initFacebookPixel = (pixelId: string) => {
    if (!checkConsent('marketing')) return;
    
    // Basic Facebook Pixel initialization
    const script = document.createElement('script');
    script.text = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${pixelId}');
        fbq('track', 'PageView');
    `;
    document.head.appendChild(script);
};

// Hotjar helpers (disabled for now since you're only using GA)
export const initHotjar = (hjid: string) => {
    if (!checkConsent('analytics')) return;
    
    const script = document.createElement('script');
    script.text = `
        (function(h,o,t,j,a,r){
            h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
            h._hjSettings={hjid:${hjid},hjsv:6};
            a=o.getElementsByTagName('head')[0];
            r=o.createElement('script');r.async=1;
            r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
            a.appendChild(r);
        })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
    `;
    document.head.appendChild(script);
};

// Cookie management
export const setCookie = (name: string, value: string, days: number = 365) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax;Secure`;
};

export const getCookie = (name: string): string | null => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
};

export const deleteCookie = (name: string) => {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/`;
};

// Clear cookies based on consent
export const clearNonConsentedCookies = () => {
    const consent = localStorage.getItem('anathoth_cookie_consent');
    if (!consent) return;
    
    try {
        const parsed = JSON.parse(consent);
        
        // Clear analytics cookies if not consented
        if (!parsed.analytics) {
            deleteCookie('_ga');
            deleteCookie('_gid');
            deleteCookie('_gat');
            deleteCookie('_gat_gtag_' + 'G_BM9K275DJ5'.replace(/-/g, '_'));
        }
        
        // Clear marketing cookies if not consented
        if (!parsed.marketing) {
            deleteCookie('_fbp');
            deleteCookie('_fbc');
            deleteCookie('fr');
        }
    } catch (error) {
        console.error('Error clearing cookies:', error);
    }
};

// Initialize Google Analytics with your actual ID
export const initializeAnalytics = () => {
    const consent = localStorage.getItem('anathoth_cookie_consent');
    if (!consent) return;
    
    try {
        const parsed = JSON.parse(consent);
        
        if (parsed.analytics) {
            // My Google Analytics ID
            const GA_ID = 'G-BM9K275DJ5';
            initGoogleAnalytics(GA_ID);
        }
        
        // Disabled other analytics services for now
        // if (parsed.marketing) {
        //     const FB_PIXEL_ID = process.env.REACT_APP_FB_PIXEL_ID || 'FB_PIXEL_ID';
        //     if (FB_PIXEL_ID !== 'FB_PIXEL_ID') {
        //         initFacebookPixel(FB_PIXEL_ID);
        //     }
        // }
    } catch (error) {
        console.error('Error initializing analytics:', error);
    }
};

// Listen for consent changes and initialize analytics
if (typeof window !== 'undefined') {
    window.addEventListener('cookieConsentUpdated', () => {
        clearNonConsentedCookies();
        initializeAnalytics();
    });
    
    // Initialize on page load if consent already exists
    document.addEventListener('DOMContentLoaded', () => {
        initializeAnalytics();
    });
} 